// =============================================================================
// MeuDriver — PwaInstallBanner (Batch K3)
// Banner dismissível que convida o usuário a instalar o PWA.
// Aparece somente após 2 visitas (contador em localStorage).
// =============================================================================

import { useEffect, useState } from 'react'
import { X, Download } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { usePwaInstall } from '@/hooks/usePwaInstall'

const STORAGE_KEY_VISITS    = 'meudriver_pwa_visits'
const STORAGE_KEY_DISMISSED = 'meudriver_pwa_banner_dismissed'
const REQUIRED_VISITS = 2

function incrementVisitCount(): number {
  try {
    const current = parseInt(localStorage.getItem(STORAGE_KEY_VISITS) ?? '0', 10)
    const next    = current + 1
    localStorage.setItem(STORAGE_KEY_VISITS, String(next))
    return next
  } catch {
    return 0
  }
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_DISMISSED) === '1'
  } catch {
    return false
  }
}

function dismiss(): void {
  try {
    localStorage.setItem(STORAGE_KEY_DISMISSED, '1')
  } catch {
    // silencioso
  }
}

export function PwaInstallBanner() {
  const { canInstall, install, isInstalled } = usePwaInstall()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Incrementa o contador de visitas uma vez por sessão de mount
    const visits = incrementVisitCount()
    if (visits >= REQUIRED_VISITS && !isDismissed() && !isInstalled) {
      setVisible(true)
    }
  }, [isInstalled])

  // Se o usuário acabou de instalar, esconde o banner
  useEffect(() => {
    if (isInstalled) setVisible(false)
  }, [isInstalled])

  // Não renderiza se não pode instalar ou não deve aparecer
  if (!canInstall || !visible) return null

  function handleInstall() {
    install().then((accepted) => {
      if (accepted) setVisible(false)
    })
  }

  function handleDismiss() {
    dismiss()
    setVisible(false)
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="pwa-install-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto"
          data-testid="pwa-install-banner"
        >
          <div className="flex items-start gap-3 rounded-2xl border border-brand-200 bg-white p-4 shadow-lg">
            <div className="h-10 w-10 shrink-0 rounded-xl bg-brand-600 flex items-center justify-center">
              <Download className="h-5 w-5 text-white" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-tight">
                Instale o MeuDriver no seu celular
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Acesso rápido, sem abrir o navegador!
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  className="h-8 text-xs px-3"
                  onClick={handleInstall}
                  data-testid="pwa-install-btn"
                >
                  Instalar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs px-3 text-muted-foreground"
                  onClick={handleDismiss}
                  data-testid="pwa-dismiss-btn"
                >
                  Agora não
                </Button>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="h-6 w-6 shrink-0 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Fechar"
              data-testid="pwa-close-btn"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
